import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Wrench, Users, Clock, Award, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import useSimpleCounter from '../../hooks/useSimpleCounter';
import './HeroSection.css';

// Import hero background images (alternate images from service folders for banner)
import heroIntro from '../../images/Workshop/Workshop1.jpeg';
import safetyvalve from '../../images/SafetyValve/SafetyValve2.png';
import valveservices from '../../images/ValveService/ValveService2.jpeg';
import actuators from '../../images/Actuator/ElectricActuator1.png';
import sparepart from '../../images/SpareParts/SpareService2.jpeg';

/** Module-level slide list so the auto-advance effect uses stable deps and never stacks intervals. */
const HERO_SLIDES = [
  {
    id: 0,
    title: 'Your Trusted Partner in Industrial Flow Control',
    description:
      'Welcome to KirBOO Flowsafe Pvt Ltd — trusted since 2005. Founded as KirBOO Flowsafe; incorporated as Pvt Ltd in 2022. Your partner for high-pressure valves, electric & pneumatic actuators, Pump Services, and spare parts with 25+ years of experience.',
    icon: Shield,
    features: [
      'All brands and sizes supported',
      'Emergency repair services',
      'Certified technicians',
      '24/7 technical assistance'
    ],
    path: '/services',
    isIntro: true,
    backgroundImage: heroIntro
  },
  {
    id: 1,
    title: 'Safety Valve and Breather Valve',
    description: 'Comprehensive repair and maintenance for all brands',
    icon: Shield,
    features: [
      'All brands and sizes supported',
      'Pressure testing and calibration',
      'Emergency repair services',
      'Certified technicians'
    ],
    path: '/services/safety-valve',
    backgroundImage: safetyvalve
  },
  {
    id: 2,
    title: 'Valve Services',
    description: '150# - 4500# pressure class support',
    icon: Wrench,
    features: [
      'Gate, Globe, Check valves',
      'High pressure applications',
      'On-site maintenance',
      'Quality assurance'
    ],
    path: '/services/valve-service',
    backgroundImage: valveservices
  },
  {
    id: 3,
    title: 'Actuators and positioners services',
    description: 'Electric & pneumatic actuator maintenance',
    icon: Zap,
    features: ['Electric actuators', 'Pneumatic actuators', 'Control systems', '24/7 support'],
    path: '/services/actuator',
    backgroundImage: actuators
  },
  {
    id: 4,
    title: 'Spare Parts',
    description: 'Genuine parts support across India',
    icon: Shield,
    features: [
      'Original manufacturer parts',
      'Fast delivery nationwide',
      'Competitive pricing',
      'Quality guarantee'
    ],
    path: '/services/spares',
    backgroundImage: sparepart
  }
];

const SLIDE_COUNT = HERO_SLIDES.length;

// Animated Hero Stat Card Component
const AnimatedHeroStatCard = ({ stat, index }) => {
  const [animatedValue, elementRef] = useSimpleCounter(
    stat.number, 
    2000, 
    index * 150 // Staggered delay: 0ms, 150ms, 300ms, 450ms
  );
  const [isAnimating, setIsAnimating] = useState(false);

  // Trigger completion animation when counter reaches target
  useEffect(() => {
    if (animatedValue === stat.number) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 800);
      return () => clearTimeout(timer);
    }
  }, [animatedValue, stat.number]);

  return (
    <motion.div
      ref={elementRef}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
      className="hero-stat-card"
    >
      <div className="hero-stat-icon">
        {React.createElement(stat.icon, { className: "w-6 h-6 text-white" })}
      </div>
      <div className={`hero-stat-number ${isAnimating ? 'animating' : ''}`}>
        {animatedValue}
      </div>
      <div className="hero-stat-title">{stat.title}</div>
      <div className="hero-stat-description">{stat.description}</div>
    </motion.div>
  );
};

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const stats = [
    { icon: Users, number: "500+", title: "Happy Clients", description: "Satisfied customers across India" },
    { icon: Clock, number: "24/7", title: "Support Available", description: "Round-the-clock technical assistance" },
    { icon: Award, number: "25+", title: "Years Experience", description: "Industry expertise and reliability" },
    { icon: CheckCircle, number: "1000+", title: "Projects Completed", description: "Successful installations and repairs" }
  ];

  // Single interval only ([] deps); avoids stacked timers from effect re-runs (e.g. Strict Mode / churn).
  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDE_COUNT);
    }, 8000);
    return () => window.clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % SLIDE_COUNT);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  };

  return (
    <section className="hero-section">
      {/* Dynamic Background Images */}
      <div 
        className="hero-background-slide"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(30, 58, 138, 0.7), rgba(59, 130, 246, 0.6)), url(${HERO_SLIDES[currentSlide].backgroundImage})`
        }}
      >
        <div className="hero-background-overlay"></div>
      </div>
      
      {/* Background Pattern */}
      <div className="hero-background-pattern">
        <div className="hero-pattern-overlay"></div>
      </div>

      {/* Floating Elements */}
      <div className="hero-floating-elements">
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="hero-floating-element-1"
        />
        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="hero-floating-element-2"
        />
      </div>

      <div className="container-custom px-4 relative z-10">
        <div className="hero-content-grid">
          {/* Left Content - Featured Service Carousel */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-left-content"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="hero-featured-service"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="hero-badge"
                >
                  {React.createElement(HERO_SLIDES[currentSlide].icon, { className: "w-4 h-4 mr-2" })}
                  {HERO_SLIDES[currentSlide].isIntro ? "Trusted since 2005" : "Featured Service"}
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="hero-title"
                >
                  {HERO_SLIDES[currentSlide].isIntro ? (
                    <>
                      Your Trusted Partner in{' '}
                      <span className="hero-title-highlight">Industrial Flow Control</span>
                    </>
                  ) : (
                    HERO_SLIDES[currentSlide].title
                  )}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="hero-description"
                >
                  {HERO_SLIDES[currentSlide].description}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="hero-features"
                >
                  {HERO_SLIDES[currentSlide].features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
                      className="hero-feature-item"
                    >
                      <div className="hero-feature-dot"></div>
                      <span>{feature}</span>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  className="hero-buttons"
                >
                  <Link to={HERO_SLIDES[currentSlide].path} className="hero-primary-button">
                    {HERO_SLIDES[currentSlide].isIntro ? "Explore Services" : "Learn More"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <Link to="/contact" className="hero-secondary-button">
                    Get Quote
                  </Link>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="hero-trust-indicators"
            >
              <div className="hero-trust-item">
                <div className="hero-trust-dot"></div>
                <span>24/7 Support</span>
              </div>
              <div className="hero-trust-item">
                <div className="hero-trust-dot"></div>
                <span>Certified Technicians</span>
              </div>
              <div className="hero-trust-item">
                <div className="hero-trust-dot"></div>
                <span>Genuine Parts</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Service Icons and Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-right-content"
          >
            {/* Stats Cards */}
            <div className="hero-stats-grid">
              {stats.map((stat, index) => (
                <AnimatedHeroStatCard key={index} stat={stat} index={index} />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Navigation Controls */}
        <div className="hero-navigation">
          <button type="button" onClick={prevSlide} className="hero-nav-button" aria-label="Previous slide">
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="hero-dots">
            {HERO_SLIDES.map((_, index) => (
              <button
                type="button"
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`hero-dot ${currentSlide === index ? 'hero-dot-active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={currentSlide === index ? 'true' : undefined}
              />
            ))}
          </div>
          
          <button type="button" onClick={nextSlide} className="hero-nav-button" aria-label="Next slide">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="hero-scroll-indicator"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="hero-scroll-container"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="hero-scroll-dot"
          />
        </motion.div>
      </motion.div> */}
    </section>
  );
};

export default HeroSection;
